import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Stack, IconButton, Chip, Tooltip, Skeleton, Divider } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImportCutoffAPI from "../../../api/ImportCutoffAPI";
import ConfirmationModal from "../../../components/confirmation-modal";

// "2026-07" -> "July 2026"
const formatPeriod = (period) => {
	if (!period) return "";
	const [year, month] = period.split("-").map(Number);
	if (!year || !month) return period;
	return new Date(year, month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

// The cutoff window a period covers, so the label is unambiguous: a period runs
// from the 16th of the previous month to the 15th of its own.
const formatWindow = (period) => {
	if (!period) return "";
	const [year, month] = period.split("-").map(Number);
	if (!year || !month) return "";

	const end = new Date(year, month - 1, 15);
	const start = new Date(year, month - 2, 16);
	const opts = { month: "short", day: "numeric" };

	return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", { ...opts, year: "numeric" })}`;
};

const formatImportedAt = (value) => {
	if (!value) return null;
	const d = new Date(value.replace(" ", "T"));
	return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const ImportedCutoffs = ({ showSnackbar, refreshKey = 0 }) => {
	const [periods, setPeriods] = useState([]);
	const [loading, setLoading] = useState(true);
	const [pendingDelete, setPendingDelete] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			setPeriods(await ImportCutoffAPI.list());
		} catch (error) {
			console.error(error);
			showSnackbar?.(error?.message || "Could not load the imported cutoffs.", "error");
			setPeriods([]);
		} finally {
			setLoading(false);
		}
	}, [showSnackbar]);

	// refreshKey changes after a successful import, so a newly imported cutoff
	// appears here without a page reload.
	useEffect(() => {
		load();
	}, [load, refreshKey]);

	const handleConfirmDelete = async () => {
		if (!pendingDelete) return;

		setDeleting(true);
		try {
			const result = await ImportCutoffAPI.remove(pendingDelete.period);
			showSnackbar?.(result?.message || `Removed the ${formatPeriod(pendingDelete.period)} cutoff.`, "success");
			setPendingDelete(null);
			await load();
		} catch (error) {
			console.error(error);
			showSnackbar?.(error?.message || "Could not delete that cutoff.", "error");
		} finally {
			setDeleting(false);
		}
	};

	return (
		<Box sx={{ mt: 4 }}>
			<Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>
				Imported cutoffs
			</Typography>
			<Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>
				Every cutoff currently held in the portal. Deleting one removes its book of business and the commissions computed from it.
			</Typography>

			<Box sx={{ border: "1px solid #E8EDF2", borderRadius: "12px", overflow: "hidden" }}>
				{loading ? (
					[0, 1, 2].map((i) => (
						<Box key={i} sx={{ px: 2, py: 1.5, borderBottom: i < 2 ? "1px solid #F0F4F8" : "none" }}>
							<Skeleton variant="text" width="40%" height={20} />
							<Skeleton variant="text" width="60%" height={16} />
						</Box>
					))
				) : periods.length === 0 ? (
					<Box sx={{ px: 2, py: 4, textAlign: "center" }}>
						<Typography sx={{ fontSize: 13, color: "text.secondary" }}>No cutoffs have been imported yet.</Typography>
					</Box>
				) : (
					periods.map((entry, i) => {
						const importedAt = formatImportedAt(entry.first_imported_at);

						return (
							<Box key={entry.period}>
								{i > 0 && <Divider sx={{ borderColor: "#F0F4F8" }} />}
								<Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.5 }}>
									<Box flex={1} minWidth={0}>
										<Stack direction="row" alignItems="center" spacing={1}>
											<Typography sx={{ fontSize: 14, fontWeight: 700 }}>{formatPeriod(entry.period)}</Typography>
											<Chip
												label={entry.period}
												size="small"
												sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#EBF5FF", color: "#1D54A2" }}
											/>
										</Stack>
										<Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>
											{formatWindow(entry.period)} · {entry.policy_rows.toLocaleString()} polic
											{entry.policy_rows === 1 ? "y" : "ies"} · {entry.agent_count.toLocaleString()} agent
											{entry.agent_count === 1 ? "" : "s"}
											{importedAt ? ` · imported ${importedAt}` : ""}
										</Typography>
									</Box>

									<Tooltip title={`Delete the ${formatPeriod(entry.period)} cutoff`}>
										<IconButton
											size="small"
											onClick={() => setPendingDelete(entry)}
											sx={{
												color: "#C62828",
												backgroundColor: "transparent !important",
												"&:hover": { backgroundColor: "#FFEBEE !important" },
											}}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</Stack>
							</Box>
						);
					})
				)}
			</Box>

			<ConfirmationModal
				open={Boolean(pendingDelete)}
				onClose={() => setPendingDelete(null)}
				onConfirm={handleConfirmDelete}
				isLoading={deleting}
				title={`Delete the ${formatPeriod(pendingDelete?.period)} cutoff?`}
				description={
					pendingDelete
						? `This removes ${pendingDelete.policy_rows.toLocaleString()} policy row${
								pendingDelete.policy_rows === 1 ? "" : "s"
							} for ${pendingDelete.agent_count.toLocaleString()} agent${
								pendingDelete.agent_count === 1 ? "" : "s"
							}, along with every commission, override and payout figure computed for that cutoff. Other cutoffs are not affected, and agents keep their accounts, hierarchy and current book of business. Commissions for the most recent remaining cutoff are recomputed afterwards. This cannot be undone from the portal — only re-importing that month's file will bring it back.`
						: ""
				}
				confirmText="Delete cutoff"
				confirmColor="#DC2626 !important"
			/>
		</Box>
	);
};

export default ImportedCutoffs;
